import {
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { MSG } from '../content/messages.ts';
import '../styles/auth.css';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> & {
  /**
   * Visible label, rendered as <label for> beside (not around) the toggle and the Caps Lock notice: text nested in
   * a label becomes part of the field's accessible name ("Senha Mostrar senha Caps Lock ativado").
   */
  label: ReactNode;
  /** Shown under the field (hint or field error); point aria-describedby at it. */
  children?: ReactNode;
};

/**
 * Password field with a show/hide toggle and a Caps Lock notice. The toggle swaps only `type`: value,
 * autocomplete, focus and caret stay as they were, so password managers and the person's place in the text survive.
 */
export function PasswordInput({
  label,
  children,
  id,
  onKeyDown,
  onKeyUp,
  onBlur,
  'aria-describedby': describedBy,
  ...rest
}: Props) {
  const generated = useId();
  const inputId = id ?? generated;
  const capsId = `${inputId}-caps`;
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<[number, number] | null>(null);
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  // Some browsers move the caret to the end when `type` changes: put it back where it was.
  useEffect(() => {
    const input = ref.current;
    const saved = caret.current;
    caret.current = null;
    if (input && saved && document.activeElement === input) input.setSelectionRange(saved[0], saved[1]);
  }, [visible]);

  const readCapsLock = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState('CapsLock'));

  return (
    <div className="password-field">
      <label htmlFor={inputId}>{label}</label>
      <div className="password-box">
        <div className="password-control">
          <input
            {...rest}
            id={inputId}
            ref={ref}
            type={visible ? 'text' : 'password'}
            aria-describedby={[describedBy, capsLock ? capsId : null].filter(Boolean).join(' ') || undefined}
            onKeyDown={(e) => {
              readCapsLock(e);
              onKeyDown?.(e);
            }}
            onKeyUp={(e) => {
              readCapsLock(e);
              onKeyUp?.(e);
            }}
            onBlur={(e) => {
              setCapsLock(false);
              onBlur?.(e);
            }}
          />
          <button
            type="button"
            className="password-toggle"
            aria-label={visible ? MSG.password.hide : MSG.password.show}
            aria-pressed={visible}
            aria-controls={inputId}
            // A mouse or touch press must not move the focus out of the field (the caret would be lost).
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const input = ref.current;
              if (input && document.activeElement === input && input.selectionStart !== null) {
                caret.current = [input.selectionStart, input.selectionEnd ?? input.selectionStart];
              }
              setVisible((v) => !v);
            }}
          >
            {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
          </button>
        </div>
        {/* Always rendered (empty when off): a live region must exist before its content changes to be announced. */}
        <span id={capsId} className="caps-notice" role="status" aria-live="polite">
          {capsLock ? MSG.password.capsLock : ''}
        </span>
      </div>
      {children}
    </div>
  );
}
